import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  fetchFlowConfig,
  fetchConfidencePreview,
  updateFlowConfidence,
  updateFlowInput,
  updatePreAnalysisRules,
  fetchTenantConfig,
  fetchLessons,
  updateLesson,
  deleteLesson,
} from "../api/client";
import type { FlowConfig, ConfidencePreview, AuditEntry, PaginatedResponse, PreAnalysisRule } from "../api/types";
import { useToast } from "../context/ToastContext";

type TabId = "confidence" | "matching" | "input" | "output" | "extraction" | "rules" | "lessons";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "confidence", label: "Confidence", icon: "tune" },
  { id: "matching", label: "Matching", icon: "lan" },
  { id: "input", label: "Input", icon: "input" },
  { id: "output", label: "Output", icon: "output" },
  { id: "extraction", label: "Estrazione", icon: "auto_fix_high" },
  { id: "rules", label: "Regole Pre-analisi", icon: "rule" },
  { id: "lessons", label: "Apprendimenti", icon: "school" },
];

const STRATEGIES = [
  { value: "min_matched", label: "Minimo matched" },
  { value: "key_entities", label: "Key entities" },
  { value: "weighted", label: "Media pesata" },
];

// Shared "unsaved changes" banner style (InputTab + RulesTab)
const dirtyBannerStyle: CSSProperties = {
  display: "flex", gap: "var(--space-3)", marginTop: "var(--space-4)",
  padding: "var(--space-3)", background: "var(--indaco-brand-primary-light)",
  borderRadius: "var(--border-radius-lg)", alignItems: "center",
};

export function FlowSettings() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [config, setConfig] = useState<FlowConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("confidence");

  // Confidence form state
  const [strategy, setStrategy] = useState("min_matched");
  const [keyEntities, setKeyEntities] = useState<string[]>([]);
  const [threshold, setThreshold] = useState(999);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Preview
  const [preview, setPreview] = useState<ConfidencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Available entity types from tenant config
  const [availableEntities, setAvailableEntities] = useState<string[]>([]);

  // Original values (for cancel)
  const origRef = useRef({ strategy: "", keyEntities: [] as string[], threshold: 999 });

  useEffect(() => {
    if (!flowId) return;
    setLoading(true);
    Promise.all([fetchFlowConfig(flowId), fetchTenantConfig()])
      .then(([fc, tc]) => {
        setConfig(fc);
        setStrategy(fc.confidence.strategy);
        setKeyEntities([...fc.confidence.key_entities]);
        setThreshold(fc.confidence.threshold_auto_approve);
        origRef.current = {
          strategy: fc.confidence.strategy,
          keyEntities: [...fc.confidence.key_entities],
          threshold: fc.confidence.threshold_auto_approve,
        };
        setAvailableEntities(tc.entity_types.map((et) => et.key));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [flowId]);

  // Load preview on threshold change with debounce
  const loadPreview = useCallback(
    (t: number) => {
      if (!flowId || t >= 999) {
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      fetchConfidencePreview(flowId, t)
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    },
    [flowId],
  );

  const handleThresholdChange = (val: number) => {
    setThreshold(val);
    setDirty(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadPreview(val), 300);
  };

  // Initial preview load when config is ready
  useEffect(() => {
    if (config && config.confidence.threshold_auto_approve < 999) {
      loadPreview(config.confidence.threshold_auto_approve);
    }
  }, [config, loadPreview]);

  const handleSave = async () => {
    if (!flowId) return;
    setSaving(true);
    try {
      await updateFlowConfidence(flowId, {
        strategy,
        key_entities: keyEntities,
        threshold_auto_approve: threshold,
      });
      origRef.current = { strategy, keyEntities: [...keyEntities], threshold };
      setDirty(false);
      addToast("success", "Configurazione confidence salvata");
    } catch (e: unknown) {
      addToast("error", `Errore salvataggio: ${e instanceof Error ? e.message : "sconosciuto"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setStrategy(origRef.current.strategy);
    setKeyEntities([...origRef.current.keyEntities]);
    setThreshold(origRef.current.threshold);
    setDirty(false);
    if (origRef.current.threshold < 999) {
      loadPreview(origRef.current.threshold);
    } else {
      setPreview(null);
    }
  };

  const addKeyEntity = (key: string) => {
    if (!keyEntities.includes(key)) {
      setKeyEntities([...keyEntities, key]);
      setDirty(true);
    }
  };

  const removeKeyEntity = (key: string) => {
    setKeyEntities(keyEntities.filter((k) => k !== key));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="page-container">
        <div className="card" style={{ padding: "var(--space-6)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ verticalAlign: "middle", marginRight: 8 }}>error</span>
          {error || "Flusso non trovato"}
        </div>
      </div>
    );
  }

  const thresholdDisplay = threshold >= 999 ? "Spento" : threshold.toFixed(2);

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="flow-breadcrumb">
        <button className="btn-link" onClick={() => navigate("/settings")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
          Configurazione
        </button>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>chevron_right</span>
        <span style={{ fontWeight: "var(--font-weight-semibold)" }}>{config.label}</span>
      </div>

      <h1 className="page-title" style={{ marginTop: "var(--space-2)" }}>{config.label}</h1>

      {/* Tab bar */}
      <div className="flow-tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`flow-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{tab.icon}</span>
            <span className="flow-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="card" style={{ padding: "var(--space-6)", marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        {activeTab === "confidence" && (
          <ConfidenceTab
            strategy={strategy}
            keyEntities={keyEntities}
            threshold={threshold}
            thresholdDisplay={thresholdDisplay}
            availableEntities={availableEntities}
            preview={preview}
            previewLoading={previewLoading}
            dirty={dirty}
            saving={saving}
            onStrategyChange={(v) => { setStrategy(v); setDirty(true); }}
            onThresholdChange={handleThresholdChange}
            onAddKeyEntity={addKeyEntity}
            onRemoveKeyEntity={removeKeyEntity}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
        {activeTab === "input" && <InputTab config={config} flowId={flowId!} onReload={() => {
          fetchFlowConfig(flowId!).then(setConfig).catch(() => {});
        }} />}
        {activeTab === "output" && <OutputTab config={config} />}
        {activeTab === "matching" && <MatchingTab config={config} />}
        {activeTab === "extraction" && <ExtractionTab config={config} />}
        {activeTab === "rules" && <RulesTab config={config} flowId={flowId!} onReload={() => {
          fetchFlowConfig(flowId!).then(setConfig).catch(() => {});
        }} />}
        {activeTab === "lessons" && <LessonsTab />}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function ConfidenceTab({
  strategy, keyEntities, threshold, thresholdDisplay,
  availableEntities, preview, previewLoading, dirty, saving,
  onStrategyChange, onThresholdChange, onAddKeyEntity, onRemoveKeyEntity,
  onSave, onCancel,
}: {
  strategy: string;
  keyEntities: string[];
  threshold: number;
  thresholdDisplay: string;
  availableEntities: string[];
  preview: ConfidencePreview | null;
  previewLoading: boolean;
  dirty: boolean;
  saving: boolean;
  onStrategyChange: (v: string) => void;
  onThresholdChange: (v: number) => void;
  onAddKeyEntity: (k: string) => void;
  onRemoveKeyEntity: (k: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const addableEntities = availableEntities.filter((e) => !keyEntities.includes(e));
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);

  return (
    <div>
      {/* Strategy */}
      <div className="flow-field">
        <label className="flow-label">Strategia confidence</label>
        <select
          className="flow-select"
          value={strategy}
          onChange={(e) => onStrategyChange(e.target.value)}
        >
          {STRATEGIES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Key entities */}
      <div className="flow-field">
        <label className="flow-label">Key entities</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "center" }}>
          {keyEntities.map((ke) => (
            <span key={ke} className="flow-chip">
              {ke}
              <button
                className="flow-chip-remove"
                onClick={() => onRemoveKeyEntity(ke)}
                title={`Rimuovi ${ke}`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
              </button>
            </span>
          ))}
          {addableEntities.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                className="btn-toggle-sm btn-outline"
                onClick={() => setShowEntityDropdown(!showEntityDropdown)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                Aggiungi
              </button>
              {showEntityDropdown && (
                <div className="flow-dropdown">
                  {addableEntities.map((e) => (
                    <button
                      key={e}
                      className="flow-dropdown-item"
                      onClick={() => { onAddKeyEntity(e); setShowEntityDropdown(false); }}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Threshold slider */}
      <div className="flow-field">
        <label className="flow-label">
          Soglia auto-approve:{" "}
          <strong>{thresholdDisplay}</strong>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>0</span>
          <input
            type="range"
            className="flow-slider"
            min={0}
            max={1}
            step={0.01}
            value={threshold >= 999 ? 1 : threshold}
            onChange={(e) => onThresholdChange(parseFloat(e.target.value))}
          />
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>1.0</span>
          <button
            className={`btn-toggle-sm ${threshold >= 999 ? "btn-outline active" : "btn-outline"}`}
            onClick={() => onThresholdChange(threshold >= 999 ? 0.85 : 999)}
            title={threshold >= 999 ? "Attiva auto-approve" : "Spegni auto-approve"}
          >
            {threshold >= 999 ? "Attiva" : "Spegni"}
          </button>
        </div>
      </div>

      {/* Preview */}
      {threshold < 999 && (
        <div className="flow-preview-box">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-info)" }}>preview</span>
            <strong>Preview impatto</strong>
            {previewLoading && <div className="spinner spinner-sm" />}
          </div>
          {preview ? (
            <>
              <div style={{ fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)" }}>
                Su <strong>{preview.total_requests}</strong> richieste (ultimi {preview.period_days} giorni):
              </div>
              <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
                <div className="preview-bar-container">
                  <div
                    className="preview-bar preview-bar-auto"
                    style={{
                      width: preview.total_requests > 0
                        ? `${(preview.would_auto_approve / preview.total_requests) * 100}%`
                        : "0%",
                    }}
                  />
                  <div
                    className="preview-bar preview-bar-manual"
                    style={{
                      width: preview.total_requests > 0
                        ? `${(preview.would_need_review / preview.total_requests) * 100}%`
                        : "0%",
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "var(--space-6)", fontSize: "var(--font-size-sm)" }}>
                <span
                  style={{ cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                  title="Filtra richieste auto-approvate"
                  onClick={() => navigate(`/?conf_min=${Math.ceil(threshold)}`)}
                >
                  <span className="preview-dot preview-dot-auto" />
                  Auto-approvate: <strong>{preview.would_auto_approve}</strong>{" "}
                  ({preview.total_requests > 0 ? Math.round((preview.would_auto_approve / preview.total_requests) * 100) : 0}%)
                </span>
                <span
                  style={{ cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                  title="Filtra richieste in revisione manuale"
                  onClick={() => navigate(`/?conf_max=${Math.floor(threshold) - 1}`)}
                >
                  <span className="preview-dot preview-dot-manual" />
                  Revisione manuale: <strong>{preview.would_need_review}</strong>
                </span>
              </div>
              {preview.auto_approved_with_corrections > 0 && (
                <div className="preview-warning">
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>warning</span>
                  <strong>{preview.auto_approved_with_corrections}</strong> richieste sarebbero state
                  approvate automaticamente pur avendo ricevuto correzioni
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "var(--indaco-gray-600)", fontSize: "var(--font-size-sm)" }}>
              Nessun dato disponibile per la preview
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flow-actions">
        <button className="btn btn-primary" onClick={onSave} disabled={!dirty || saving}>
          {saving ? <><div className="spinner spinner-sm" style={{ marginRight: 6 }} />Salvataggio…</> : "Salva"}
        </button>
        <button className="btn btn-outline" onClick={onCancel} disabled={!dirty || saving}>
          Annulla
        </button>
      </div>
    </div>
  );
}

function InputTab({ config, flowId, onReload }: { config: FlowConfig; flowId: string; onReload: () => void }) {
  const { addToast } = useToast();
  const mailboxes = config.input.mailboxes;

  // Local state: map of mailbox email → forwarder_addresses (editable copy)
  const [localFwd, setLocalFwd] = useState<Record<string, string[]>>(() => {
    const m: Record<string, string[]> = {};
    for (const mb of mailboxes) m[mb.email] = [...(mb.forwarder_addresses ?? [])];
    return m;
  });
  const [newAddr, setNewAddr] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Sync when config changes externally
  useEffect(() => {
    const m: Record<string, string[]> = {};
    for (const mb of mailboxes) m[mb.email] = [...(mb.forwarder_addresses ?? [])];
    setLocalFwd(m);
  }, [mailboxes]);

  // Dirty check
  const isDirty = mailboxes.some((mb) => {
    const orig = [...(mb.forwarder_addresses ?? [])].sort().join(",");
    const curr = [...(localFwd[mb.email] ?? [])].sort().join(",");
    return orig !== curr;
  });

  function addForwarder(email: string) {
    const raw = (newAddr[email] ?? "").trim().toLowerCase();
    if (!raw || !raw.includes("@")) return;
    setLocalFwd((p) => {
      const list = p[email] ?? [];
      if (list.includes(raw)) return p;
      return { ...p, [email]: [...list, raw] };
    });
    setNewAddr((p) => ({ ...p, [email]: "" }));
  }

  function removeForwarder(email: string, addr: string) {
    setLocalFwd((p) => ({ ...p, [email]: (p[email] ?? []).filter((a) => a !== addr) }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = mailboxes.map((mb) => ({
        email: mb.email,
        forwarder_addresses: localFwd[mb.email] ?? [],
      }));
      await updateFlowInput(flowId, payload);
      addToast("success", "Indirizzi forwarder aggiornati");
      onReload();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    const m: Record<string, string[]> = {};
    for (const mb of mailboxes) m[mb.email] = [...(mb.forwarder_addresses ?? [])];
    setLocalFwd(m);
    setNewAddr({});
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>
        Mailbox configurate
      </h3>
      {mailboxes.length === 0 ? (
        <p style={{ color: "var(--indaco-gray-600)" }}>Nessuna mailbox configurata</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {mailboxes.map((mb, i) => (
            <div key={i} className="flow-readonly-card">
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: mb.enabled ? "var(--indaco-success)" : "var(--indaco-gray-500)" }}>
                  {mb.enabled ? "mail" : "mail_off"}
                </span>
                <strong>{mb.email}</strong>
              </div>
              <div style={{ display: "flex", gap: "var(--space-6)", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginTop: "var(--space-2)" }}>
                <span>Polling: {mb.polling_interval}s</span>
                <span>Cartella: {mb.folder}</span>
                <span>Stato: {mb.enabled ? "Attiva" : "Disattivata"}</span>
              </div>

              {/* Forwarder addresses — editable */}
              <div style={{ marginTop: "var(--space-3)", paddingTop: "var(--space-2)", borderTop: "1px dashed var(--indaco-gray-200)" }}>
                <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>forward_to_inbox</span>
                  Indirizzi forwarder
                  <span style={{ fontSize: 10, color: "var(--indaco-gray-400)" }}>
                    — il sistema li riconosce come inoltri e cerca il mittente originale nell'allegato
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {(localFwd[mb.email] ?? []).map((addr) => (
                    <span key={addr} className="flow-chip">
                      {addr}
                      <button onClick={() => removeForwarder(mb.email, addr)} className="flow-chip-remove" title="Rimuovi">
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
                      </button>
                    </span>
                  ))}
                  {(localFwd[mb.email] ?? []).length === 0 && (
                    <span style={{ fontSize: 12, color: "var(--indaco-gray-400)", fontStyle: "italic" }}>Nessun forwarder configurato</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="email"
                    placeholder="nuovo@indirizzo.it"
                    value={newAddr[mb.email] ?? ""}
                    onChange={(e) => setNewAddr((p) => ({ ...p, [mb.email]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addForwarder(mb.email); } }}
                    className="form-input"
                    style={{ maxWidth: 300 }}
                  />
                  <button onClick={() => addForwarder(mb.email)} className="btn-toggle-sm btn-outline">
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
                    Aggiungi
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isDirty && (
        <div style={dirtyBannerStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>info</span>
          <span style={{ fontSize: 13, color: "var(--indaco-brand-primary)", flex: 1 }}>Hai modifiche non salvate</span>
          <button className="btn btn-sm btn-ghost" onClick={handleCancel} disabled={saving}>Annulla</button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      )}
    </div>
  );
}

function OutputTab({ config }: { config: FlowConfig }) {
  return (
    <div>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>
        Configurazione output
      </h3>
      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>description</span>
          <span>Formato: <strong>{config.output.format}</strong></span>
        </div>
      </div>
      <h4 style={{ margin: "0 0 var(--space-3)", fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-medium)" }}>
        Destinazioni
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {config.output.destinations.map((d, i) => (
          <div key={i} className="flow-readonly-card">
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 18,
                  color: d.enabled ? "var(--indaco-success)" : "var(--indaco-gray-500)",
                }}
              >
                {d.enabled ? "cloud_done" : "cloud_off"}
              </span>
              <span>{d.type}</span>
              <span
                className={`flow-status-badge ${d.enabled ? "enabled" : "disabled"}`}
              >
                {d.enabled ? "Attivo" : "Disattivato"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STEP_METHOD_LABELS: Record<string, string> = {
  exact: "Corrispondenza esatta",
  learned: "Appreso da feedback",
  impianto_unico: "Impianto unico nell'edificio",
  content_match_impianto: "Match da contenuto email",
  regola_2_impianti_elettrico: "Regola 2 impianti (elettrico)",
  filter_desc_prest: "Filtro tipo prestazione",
  filter_by_chiamante_edifici: "Edifici del chiamante",
  fuzzy: "Fuzzy (testo simile)",
  vector: "Vettoriale (embedding)",
  fallback_primo_seriale: "Fallback primo seriale",
  fallback_sender_chiamante: "Chiamante da mittente email",
};

function MatchingTab({ config }: { config: FlowConfig }) {
  const cascades = config.matching.cascades;
  const entityTypes = config.matching.entity_types;
  return (
    <div>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>
        Cascate di matching
      </h3>
      <p className="text-sm text-muted" style={{ marginBottom: "var(--space-4)" }}>
        Per ogni tipo di entità, i metodi vengono eseguiti in ordine. Il primo che trova un match con score sufficiente si ferma.
      </p>
      {entityTypes.map((et) => {
        const steps = (cascades[et] ?? []) as Record<string, unknown>[];
        return (
          <div key={et} style={{ marginBottom: "var(--space-5)" }}>
            <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)", textTransform: "capitalize" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }}>account_tree</span>
              {et}
            </div>
            {steps.length === 0 ? (
              <div className="text-sm text-muted">Nessuno step configurato</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                {steps.map((step, i) => {
                  const method = String(step.method ?? "");
                  const label = STEP_METHOD_LABELS[method] ?? method;
                  const minScore = step.min_score != null ? Number(step.min_score) : null;
                  const stopIfFound = Boolean(step.stop_if_found);
                  return (
                    <div key={i} className="flow-readonly-card" style={{ padding: "var(--space-2) var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                      <span className="text-xs text-muted" style={{ width: 20, textAlign: "right", flexShrink: 0 }}>{i + 1}.</span>
                      <span className="text-sm fw-medium" style={{ flex: 1 }}>{label}</span>
                      {minScore != null && (
                        <span className="badge badge-neutral" style={{ fontSize: "var(--font-size-xs)" }}>
                          min: {(minScore * 100).toFixed(0)}%
                        </span>
                      )}
                      {stopIfFound && (
                        <span className="badge badge-info" style={{ fontSize: "var(--font-size-xs)" }}>
                          stop se trovato
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExtractionTab({ config }: { config: FlowConfig }) {
  const { entity_types_involved, classifier_rules, prompt_template } = config.extraction;
  return (
    <div>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>
        Configurazione estrazione
      </h3>

      {/* Entity types */}
      <div style={{ marginBottom: "var(--space-5)" }}>
        <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>
          Entità estratte
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {entity_types_involved.map((et) => (
            <span key={et} className="badge badge-neutral" style={{ textTransform: "capitalize" }}>{et}</span>
          ))}
        </div>
      </div>

      {/* Classifier rules */}
      {classifier_rules.length > 0 && (
        <div style={{ marginBottom: "var(--space-5)" }}>
          <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>
            Regole di classificazione
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {classifier_rules.map((rule, i) => {
              const rType = rule.type != null ? String(rule.type) : "";
              const rPattern = rule.pattern != null ? String(rule.pattern) : "";
              const rSender = rule.sender != null ? String(rule.sender) : "";
              const rFlow = rule.flow_type != null ? String(rule.flow_type) : "";
              return (
                <div key={i} className="flow-readonly-card" style={{ padding: "var(--space-2) var(--space-3)" }}>
                  <div className="text-sm">
                    {rType && <span className="fw-medium">{rType}</span>}
                    {rPattern && <span className="text-muted"> — pattern: <code>{rPattern}</code></span>}
                    {rSender && <span className="text-muted"> — sender: {rSender}</span>}
                    {rFlow && <span className="badge badge-info" style={{ marginLeft: "var(--space-2)", fontSize: "var(--font-size-xs)" }}>{rFlow}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prompt template */}
      {prompt_template && (
        <div>
          <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>
            Template prompt LLM
          </div>
          <pre style={{
            fontSize: "var(--font-size-xs)",
            background: "var(--indaco-gray-50)",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-md)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 300,
            overflow: "auto",
            border: "1px solid var(--indaco-gray-200)",
          }}>
            {prompt_template}
          </pre>
        </div>
      )}
    </div>
  );
}

const RULE_LABELS: Record<string, { label: string; icon: string; description: string }> = {
  allarme: {
    label: "Allarmi Telecontrollo",
    icon: "notifications_active",
    description: "Intercetta email automatiche di allarme/normale da sistemi di telecontrollo. Bypass LLM con analisi deterministica.",
  },
  hyperfabbricati: {
    label: "Hyperfabbricati",
    icon: "domain",
    description: "Intercetta email dal portale Hyperfabbricati, scarica i dati dell'intervento e genera analisi deterministica senza LLM.",
  },
};

function RulesTab({ config, flowId, onReload }: { config: FlowConfig; flowId: string; onReload: () => void }) {
  const { addToast } = useToast();
  const rules = config.pre_analysis_rules ?? [];
  const [localRules, setLocalRules] = useState<PreAnalysisRule[]>(() => rules.map((r) => ({ ...r })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalRules(rules.map((r) => ({ ...r })));
  }, [config]);

  const isDirty = rules.some((r, i) => localRules[i]?.enabled !== r.enabled);

  function toggleRule(subType: string) {
    setLocalRules((prev) => prev.map((r) => r.sub_type === subType ? { ...r, enabled: !r.enabled } : r));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updatePreAnalysisRules(flowId, localRules.map((r) => ({ sub_type: r.sub_type, enabled: r.enabled })));
      addToast("success", "Regole pre-analisi aggiornate");
      onReload();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setLocalRules(rules.map((r) => ({ ...r })));
  }

  if (rules.length === 0) {
    return (
      <div>
        <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Regole Pre-analisi</h3>
        <p style={{ color: "var(--indaco-gray-600)" }}>Nessuna regola pre-analisi configurata per questo tenant.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-md)" }}>Regole Pre-analisi</h3>
      <p style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>
        Le regole deterministiche intercettano email specifiche <strong>prima</strong> dell'analisi LLM, generando risultati istantanei e a costo zero.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {localRules.map((rule) => {
          const meta = RULE_LABELS[rule.sub_type] ?? { label: rule.sub_type, icon: "rule", description: "" };
          const paramEntries = Object.entries(rule.params).filter(([k]) => !k.toLowerCase().includes("password"));
          return (
            <div key={rule.sub_type} className="flow-readonly-card" style={{
              borderLeft: `3px solid ${rule.enabled ? "var(--indaco-success)" : "var(--indaco-gray-300)"}`,
              opacity: rule.enabled ? 1 : 0.7,
              transition: "all 0.2s",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                  <span className="material-symbols-outlined" style={{
                    fontSize: 20,
                    color: rule.enabled ? "var(--indaco-success)" : "var(--indaco-gray-400)",
                  }}>{meta.icon}</span>
                  <strong style={{ fontSize: "var(--font-size-base)" }}>{meta.label}</strong>
                  <span className="badge" style={{
                    fontSize: 10, padding: "1px 6px", marginLeft: 4,
                    background: rule.enabled ? "var(--indaco-success-light)" : "var(--indaco-gray-100)",
                    color: rule.enabled ? "var(--indaco-success)" : "var(--indaco-gray-600)",
                  }}>
                    {rule.enabled ? "ATTIVA" : "DISATTIVATA"}
                  </span>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
                  <span style={{ fontSize: 12, color: "var(--indaco-gray-600)" }}>
                    {rule.enabled ? "Attiva" : "Disattivata"}
                  </span>
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={() => toggleRule(rule.sub_type)}
                    style={{ width: 18, height: 18, cursor: "pointer", accentColor: "var(--indaco-brand-primary)" }}
                  />
                </label>
              </div>
              {meta.description && (
                <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginTop: "var(--space-2)" }}>
                  {meta.description}
                </div>
              )}
              {paramEntries.length > 0 && (
                <div style={{
                  marginTop: "var(--space-3)", paddingTop: "var(--space-2)",
                  borderTop: "1px dashed var(--indaco-gray-200)",
                }}>
                  <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginBottom: 4 }}>
                    Parametri
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {paramEntries.map(([k, v]) => (
                      <span key={k} style={{
                        fontSize: 11, padding: "2px 8px", background: "var(--indaco-gray-50)",
                        borderRadius: "var(--border-radius-sm)", fontFamily: "var(--font-family-mono)",
                        border: "1px solid var(--indaco-gray-200)",
                      }}>
                        <span style={{ color: "var(--indaco-gray-500)" }}>{k}:</span>{" "}
                        <span>{String(v).startsWith("env:") ? String(v) : String(v)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isDirty && (
        <div style={dirtyBannerStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>info</span>
          <span style={{ fontSize: 13, color: "var(--indaco-brand-primary)", flex: 1 }}>Hai modifiche non salvate</span>
          <button className="btn btn-sm btn-ghost" onClick={handleCancel} disabled={saving}>Annulla</button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      )}
    </div>
  );
}

function LessonsTab() {
  const [data, setData] = useState<PaginatedResponse<AuditEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { addToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    fetchLessons(page, 20)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  function startEdit(entry: AuditEntry) {
    setEditingId(entry.id);
    setEditText(entry.lesson_learned ?? "");
    setConfirmDeleteId(null);
  }

  async function saveEdit() {
    if (!editingId || !editText.trim()) return;
    setSaving(true);
    try {
      await updateLesson(editingId, editText.trim());
      addToast("success", "Apprendimento aggiornato");
      setEditingId(null);
      load();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(id: string) {
    setSaving(true);
    try {
      await deleteLesson(id);
      addToast("info", "Apprendimento eliminato");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const totalPages = data ? Math.ceil(data.total / 20) : 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-4)" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "var(--font-size-md)" }}>Apprendimenti</h3>
          <p className="text-xs text-muted" style={{ margin: "var(--space-1) 0 0" }}>
            Correzioni operative degli operatori. Puoi modificare il testo o eliminare voci non più valide.
          </p>
        </div>
        {data && <span className="text-xs text-muted">{data.total} totali</span>}
      </div>

      {loading && !data && (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-8)" }}>
          <div className="spinner" />
        </div>
      )}

      {data && data.items.length === 0 && (
        <div style={{ textAlign: "center", padding: "var(--space-8)", color: "var(--indaco-gray-500)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, display: "block", marginBottom: "var(--space-2)" }}>school</span>
          Nessun apprendimento registrato
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="table-container" style={{ opacity: loading ? 0.6 : 1 }}>
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Operatore</th>
                <th>Tipo</th>
                <th>Dettaglio correzione</th>
                <th>Nota operatore</th>
                <th>Rif.</th>
                <th style={{ textAlign: "center" }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((entry) => {
                const d = (entry.details ?? {}) as Record<string, unknown>;
                const isEditing = editingId === entry.id;
                const isDeleting = confirmDeleteId === entry.id;

                // Extract structured info
                const corrections = (Array.isArray(d.corrections) ? d.corrections : []) as Array<Record<string, unknown>>;
                const motivoOrig = d.motivo_original != null ? String(d.motivo_original) : "";
                const motivoCorr = d.motivo_corrected != null ? String(d.motivo_corrected) : "";
                const isMotivo = entry.action === "approve" && (motivoOrig || motivoCorr);
                const isEntityCorr = corrections.length > 0;

                // Find operator note from corrections or lesson text
                const corrNotes = corrections.map((c) => String(c.note ?? "").trim()).filter(Boolean);
                const operatorNote = corrNotes.join("; ") || (entry.feedback_note ?? "");

                // Type label
                const tipoLabel = isMotivo ? "Motivo" : isEntityCorr
                  ? corrections.map((c) => {
                    const fl: Record<string, string> = { impianto: "Impianto", edificio: "Edificio", chiamante: "Chiamante" };
                    return fl[String(c.field_corrected ?? "")] ?? String(c.field_corrected ?? "");
                  }).join(", ")
                  : entry.action ?? "—";

                if (isEditing) {
                  return (
                    <tr key={entry.id}>
                      <td colSpan={7} style={{ background: "var(--indaco-gray-50)" }}>
                        <div className="text-xs text-muted" style={{ marginBottom: "var(--space-1)" }}>Modifica apprendimento:</div>
                        <textarea className="form-input" rows={3}
                          style={{ width: "100%", fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)" }}
                          value={editText} onChange={(e) => setEditText(e.target.value)} />
                        <div style={{ display: "flex", gap: "var(--space-2)" }}>
                          <button className="btn btn-primary btn-sm" disabled={saving || !editText.trim()} onClick={saveEdit}>
                            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Salva"}
                          </button>
                          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setEditingId(null)}>Annulla</button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                if (isDeleting) {
                  return (
                    <tr key={entry.id}>
                      <td colSpan={7} style={{ background: "rgba(var(--indaco-danger-rgb), 0.05)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-danger)" }}>warning</span>
                          <span className="text-sm" style={{ flex: 1 }}>Eliminare questo apprendimento?</span>
                          <button className="btn btn-danger btn-sm" disabled={saving} onClick={() => confirmDelete(entry.id)}>
                            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Elimina"}
                          </button>
                          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setConfirmDeleteId(null)}>Annulla</button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: "nowrap" }} className="text-xs text-muted">{formatLessonDate(entry.created_at)}</td>
                    <td className="text-xs">{entry.operator_name || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span className="badge" style={{
                        fontSize: "var(--font-size-xs)", padding: "2px 6px",
                        background: isMotivo ? "var(--indaco-warning-light)" : "var(--indaco-info-light)",
                        color: isMotivo ? "var(--indaco-warning)" : "var(--indaco-info)",
                      }}>{tipoLabel}</span>
                    </td>
                    <td>
                      {isMotivo && (
                        <div className="text-xs" style={{ lineHeight: 1.6 }}>
                          <div className="text-muted">Da: <em>{motivoOrig || "—"}</em></div>
                          <div>A: <strong>{motivoCorr || "—"}</strong></div>
                        </div>
                      )}
                      {isEntityCorr && corrections.map((c, i) => {
                        const origL = String(c.original_label ?? c.original_pk ?? "—");
                        const corrL = String(c.corrected_label ?? c.corrected_pk ?? "—");
                        const origPk = String(c.original_pk ?? "");
                        const corrPk = String(c.corrected_pk ?? "");
                        return (
                          <div key={i} className="text-xs" style={{ lineHeight: 1.6, marginBottom: i < corrections.length - 1 ? 4 : 0 }}>
                            <span className="text-muted">{origL}{origPk && origL !== origPk ? ` (${origPk})` : ""}</span>
                            {" → "}
                            <strong>{corrL}{corrPk && corrL !== corrPk ? ` (${corrPk})` : ""}</strong>
                          </div>
                        );
                      })}
                      {!isMotivo && !isEntityCorr && (
                        <div className="text-xs">{entry.lesson_learned || "—"}</div>
                      )}
                    </td>
                    <td className="text-xs text-muted">{operatorNote || "—"}</td>
                    <td>
                      {entry.request_id ? (
                        <Link to={`/requests/${entry.request_id}`} className="text-xs"
                          style={{ fontFamily: "var(--font-family-mono)" }}>
                          {entry.request_id.slice(0, 8)}…
                        </Link>
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <button className="btn btn-ghost btn-xs" title="Modifica" onClick={() => startEdit(entry)}
                        style={{ padding: "2px 4px" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                      </button>
                      <button className="btn btn-ghost btn-xs" title="Elimina"
                        style={{ padding: "2px 4px", color: "var(--indaco-danger)" }}
                        onClick={() => { setConfirmDeleteId(entry.id); setEditingId(null); }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prec</button>
          <span className="text-sm text-muted" style={{ padding: "var(--space-1) var(--space-2)" }}>
            {page} / {totalPages}
          </span>
          <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Succ</button>
        </div>
      )}
    </div>
  );
}

function formatLessonDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
